import { useEffect, useState } from "react";

import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import Title from "antd/es/typography/Title";

import { useGetOrganizationProperties } from "hooks/useGetOrganizationProperties";
import usePageLoading from "hooks/usePageLoading";

import {
  PageLoadingContainer,
  SkeletonHeading,
  SlowStateBanner,
  TableSkeleton,
} from "js/components/Loading";
import PropertiesTable from "js/components/Properties/PropertiesTable/PropertiesTable";
import { PaginationTable, Property } from "js/components/types";
import { Button } from "js/components/ui/button";

import "styles/components/Dashboard/TableDashboard.scss";

/**
 * Referencia estable para el caso "sin datos". NO MUTAR: se comparte entre
 * renders. Ver la nota del efecto de `data` mas abajo.
 *
 * Mismo patron que `EMPTY_PROPERTIES` en
 * `js/pages/Properties/PropertiesDashboard.tsx`: una sola solucion para el
 * mismo defecto.
 */
const EMPTY_PROPERTIES: Property[] = [];

const Properties = () => {
  const [filteredData, setFilteredData] = useState<Property[]>([]);
  const [keyPagination, setKeyPagination] = useState({
    current: 1,
    pageSize: 15,
  });
  const [pagination, setPagination] = useState<PaginationTable>({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  const screen = useBreakpoint();

  const {
    data: propertiesResponse,
    isLoading,
    refetch: refetchProperties,
  } = useGetOrganizationProperties({
    pagination: {
      current: keyPagination.current,
      pageSize: keyPagination.pageSize,
      total: 0,
    },
  });

  /**
   * Antes esto era el parametro por defecto `data = []` del destructuring, que
   * produce un array NUEVO en cada render mientras la query no tiene datos
   * (`useGetOrganizationProperties` no define `placeholderData` ni
   * `initialData`, asi que `data` es `undefined` durante la carga y de forma
   * permanente si la query queda en error).
   * El efecto de abajo dependia de `[data, ...]`, asi que corria en CADA render
   * y siempre escribia estado con `setFilteredData(data)` -> bucle infinito
   * sincrono ("Maximum update depth exceeded"), INDEPENDIENTE de TableSearch.
   * Con una constante a nivel de modulo la referencia es estable y el efecto
   * corre solo cuando los datos cambian de verdad.
   */
  const data = propertiesResponse ?? EMPTY_PROPERTIES;

  useEffect(() => {
    setFilteredData(data);
    // Idempotente: `{ ...prev }` siempre crea un objeto nuevo. Al usar la forma
    // funcional el efecto ya no lee `pagination.total`, asi que tampoco necesita
    // tenerlo en las dependencias (antes eso provocaba una segunda pasada del
    // efecto por cada cambio de total).
    setPagination((prev) =>
      prev.total === data.length ? prev : { ...prev, total: data.length },
    );
  }, [data]);

  const handleSetPagination = (newPagination: PaginationTable) => {
    setPagination(newPagination);
    setKeyPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    void refetchProperties();
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <Button
          size="sm"
          variant="outline"
          className="border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetryFetch}
        >
          Reintentar
        </Button>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-3">
      <SkeletonHeading className="h-9 w-80" />
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-4">
      <TableSkeleton columns={5} rows={6} />
    </div>
  );

  return (
    <>
      <PageLoadingContainer
        showSkeleton={showSkeleton}
        slowState={slowStateBanner}
        headerSkeleton={headerSkeleton}
        contentSkeleton={contentSkeleton}
        className="space-y-8"
      >
        <div className="space-y-6">
          <div>
            <Title level={2}>Inmuebles de la Organización</Title>
          </div>
          <div>
            <PropertiesTable
              fromOrganization={true}
              data={data}
              loading={isLoading}
              pagination={pagination}
              setPagination={handleSetPagination}
              screen={screen}
              filteredData={filteredData}
              setFilteredData={setFilteredData}
            />
          </div>
        </div>
      </PageLoadingContainer>
    </>
  );
};

export default Properties;
